<template>
	<div
		class="w-full bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-lg border border-green-800 p-3 sm:p-4"
	>
		<div
			class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
		>
			<div class="flex items-start gap-3">
				<div
					class="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-green-800 flex items-center justify-center ring-1 ring-green-700"
				>
					<svg
						class="w-5 h-5 text-white"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="1.5"
						stroke-linecap="round"
						stroke-linejoin="round"
						aria-hidden="true"
					>
						<circle cx="12" cy="12" r="9"></circle>
						<path d="M12 7v5l3 2"></path>
					</svg>
				</div>
				<div>
					<div class="text-sm font-medium">{{ label }}</div>
					<div class="text-xs text-green-100">{{ dateLabel }}</div>
				</div>
			</div>

			<div class="w-full sm:w-auto mt-1 sm:mt-0">
				<label class="sr-only" for="tz-select">Select timezone</label>
				<div class="relative w-full sm:w-auto">
					<select
						id="tz-select"
						v-model="selectedTz"
						@change="onTimezoneChange"
						class="appearance-none bg-green-800 text-white text-sm py-2 px-3 pr-8 rounded-md ring-1 ring-green-700 hover:ring-green-300 focus:outline-none focus:ring-2 focus:ring-green-300 transition-colors duration-150 w-full sm:w-auto"
						aria-label="Select timezone"
					>
						<option v-for="tz in timezones" :key="tz" :value="tz">
							{{ tz }}
						</option>
					</select>
					<svg
						class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-green-200"
						viewBox="0 0 20 20"
						fill="none"
						stroke="currentColor"
					>
						<path
							d="M6 8l4 4 4-4"
							stroke-width="1.5"
							stroke-linecap="round"
							stroke-linejoin="round"
						></path>
					</svg>
				</div>
			</div>
		</div>

		<div
			class="mt-3 flex flex-col sm:flex-row items-center sm:items-center justify-between gap-3"
		>
			<div
				class="flex flex-col sm:flex-row items-center sm:items-baseline gap-2 w-full sm:w-auto text-center sm:text-left"
			>
				<div
					class="font-mono text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight animate-[pulse_3s_ease-in-out_infinite]"
				>
					{{ largeTime }}
				</div>
				<div class="text-sm text-green-100 pt-1 sm:pt-0">
					{{ ampmLabel }}
				</div>
			</div>

			<div
				class="w-full sm:w-auto text-center sm:text-right mt-2 sm:mt-0"
			>
				<div class="text-sm font-medium">{{ shortTz }}</div>
				<div class="text-xs text-green-100">
					UTC{{ utcOffsetLabel }}
				</div>
			</div>
		</div>

		<div aria-live="polite" class="sr-only">
			Current time {{ formattedTime }} in {{ selectedTz }}
		</div>
	</div>
</template>

<script>
export default {
	name: 'TimeWidget',
	props: {
		label: { type: String, default: 'Local Time' },
		showSeconds: { type: Boolean, default: true },
	},
	data() {
		return {
			now: new Date(),
			timer: null,
			selectedTz:
				Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
			timezones: [],
		};
	},
	computed: {
		formattedTime() {
			const opts = { hour: '2-digit', minute: '2-digit' };
			if (this.showSeconds) opts.second = '2-digit';
			try {
				return this.now.toLocaleTimeString([], {
					...opts,
					timeZone: this.selectedTz,
				});
			} catch (e) {
				return this.now.toLocaleTimeString([], opts);
			}
		},
		largeTime() {
			if (this.showSeconds) {
				const t = this.formattedTime;
				const parts = t.split(' ');
				return parts[0];
			}
			return this.formattedTime;
		},
		ampmLabel() {
			const t = this.formattedTime;
			const parts = t.split(' ');
			return parts.length > 1 ? parts.slice(1).join(' ') : '';
		},
		dateLabel() {
			try {
				return this.now.toLocaleDateString([], {
					weekday: 'short',
					month: 'short',
					day: 'numeric',
					timeZone: this.selectedTz,
				});
			} catch (e) {
				return this.now.toLocaleDateString();
			}
		},
		shortTz() {
			const tz = this.selectedTz || '';
			const parts = tz.split('/');
			return parts.length
				? parts[parts.length - 1].replace('_', ' ')
				: tz;
		},
		utcOffsetLabel() {
			try {
				const dt = new Date(
					this.now.toLocaleString('en-US', {
						timeZone: this.selectedTz,
					}),
				);
				const offset = -dt.getTimezoneOffset();
				const sign = offset >= 0 ? '+' : '-';
				const abs = Math.abs(offset);
				const hours = String(Math.floor(abs / 60)).padStart(2, '0');
				const mins = String(abs % 60).padStart(2, '0');
				return `${sign}${hours}:${mins}`;
			} catch (e) {
				return '+00:00';
			}
		},
	},
	methods: {
		populateTimezones() {
			try {
				if (Intl.supportedValuesOf) {
					const zones = Intl.supportedValuesOf('timeZone');
					this.timezones = zones.slice();
					if (!this.timezones.includes(this.selectedTz))
						this.timezones.unshift(this.selectedTz);
					return;
				}
			} catch (e) {}
			this.timezones = [
				'UTC',
				'Europe/London',
				'Europe/Berlin',
				'Europe/Paris',
				'Europe/Madrid',
				'America/New_York',
				'America/Chicago',
				'America/Denver',
				'America/Los_Angeles',
				'America/Sao_Paulo',
				'Africa/Lagos',
				'Africa/Johannesburg',
				'Asia/Dubai',
				'Asia/Kolkata',
				'Asia/Shanghai',
				'Asia/Tokyo',
				'Australia/Sydney',
			];
			if (!this.timezones.includes(this.selectedTz))
				this.timezones.unshift(this.selectedTz);
		},
		onTimezoneChange() {
			this.now = new Date();
		},
	},
	mounted() {
		this.populateTimezones();
		this.timer = setInterval(() => {
			this.now = new Date();
		}, 1000);
	},
	beforeDestroy() {
		if (this.timer) clearInterval(this.timer);
	},
};
</script>

<style scoped>
select::-ms-expand {
	display: none;
}
/* subtle green-focused caret and better contrast for selectors inside the widget */
select {
	-webkit-appearance: none;
	-moz-appearance: none;
	appearance: none;
}
</style>
